import dayjs from 'dayjs';
import { z } from 'zod';
import {
  AllCharLabelSchema,
  SearchStringSchema,
  StringBooleanSchema,
  SubjectSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBLegacyExamQuestionLabelSchema,
  DBLegacyExamQuestionQuestionIdSchema,
  DBLegacyExamQuestionTypeSchema,
} from '../legacyExamQuestions/schemas';
import { DBLegacyExamTypeTypeSchema } from '../legacyExamTypes/schemas';
import { DBLegacyTopicTopicIdSchema } from '../legacyTopics/schemas';

/**
 * Shared & Helper Schemas
 */

export const DBLegacyExamSubjectExamIdSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    DBLegacyExamSubjectSchema.safeParse(parts[0]).success &&
    DBLegacyExamExamIdSchema.safeParse(parts[1]).success
  );
}, 'Must be in format subj#examId');

/**
 * DB Schemas
 */

export const DBLegacyExamSubjectSchema = SubjectSchema;
export const DBLegacyExamExamIdSchema = z.string().regex(/^[a-zA-Z0-9]{8}$/);
export const DBLegacyExamActiveSchema = StringBooleanSchema; // convert to understandable in nonDB
export const DBLegacyExamAnalyticsSchema = z.object({
  aggregateMark: z.number().min(0),
  topMark: z.number().min(0),
  uniqueAttempts: z.number().int().min(0),
});
export const DBLegacyExamCreationDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamEditDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamLabelSchema = AllCharLabelSchema.min(1).max(256);
export const DBLegacyExamMaxMarkSchema = z.number().min(0);
export const DBLegacyExamSearchStrSchema = SearchStringSchema;
export const DBLegacyExamShortLabelSchema = AllCharLabelSchema.min(1).max(16);
export const DBLegacyExamTopicsSchema = z.record(
  z.lazy(() => DBLegacyTopicTopicIdSchema),
  z.record(
    z.lazy(() => DBLegacyExamQuestionQuestionIdSchema),
    z.object({
      key: z.lazy(() => DBLegacyExamQuestionQuestionIdSchema),
      label: DBLegacyExamQuestionLabelSchema,
      type: DBLegacyExamQuestionTypeSchema,
    })
  )
);
export const DBLegacyExamTypeAttrSchema = z.lazy(() => DBLegacyExamTypeTypeSchema);

export const DBLegacyExamSchema = z.object({
  subject: DBLegacyExamSubjectSchema,
  examId: DBLegacyExamExamIdSchema,
  active: DBLegacyExamActiveSchema,
  analytics: DBLegacyExamAnalyticsSchema,
  creationDatetime: DBLegacyExamCreationDatetimeSchema,
  editDatetime: DBLegacyExamEditDatetimeSchema,
  label: DBLegacyExamLabelSchema,
  maxMark: DBLegacyExamMaxMarkSchema,
  searchStr: DBLegacyExamSearchStrSchema,
  shortLabel: DBLegacyExamShortLabelSchema,
  topics: DBLegacyExamTopicsSchema,
  type: DBLegacyExamTypeAttrSchema,
});

/**
 * Client Schemas
 */

export const LegacyExamSubjectSchema = DBLegacyExamSubjectSchema;
export const LegacyExamExamIdSchema = DBLegacyExamExamIdSchema;
export const LegacyExamActiveSchema = z.boolean();
export const LegacyExamAnalyticsSchema = DBLegacyExamAnalyticsSchema.extend({
  average: z.number().min(0),
});
export const LegacyExamCreationDatetimeSchema = DBLegacyExamCreationDatetimeSchema;
export const LegacyExamEditDatetimeSchema = DBLegacyExamEditDatetimeSchema;
export const LegacyExamLabelSchema = DBLegacyExamLabelSchema;
export const LegacyExamMaxMarkSchema = DBLegacyExamMaxMarkSchema;
export const LegacyExamSearchStrSchema = DBLegacyExamSearchStrSchema;
export const LegacyExamShortLabelSchema = DBLegacyExamShortLabelSchema;
export const LegacyExamTopicsSchema = z.record(
  z.lazy(() => DBLegacyTopicTopicIdSchema),
  z.array(
    z.object({
      key: DBLegacyExamQuestionQuestionIdSchema,
      label: DBLegacyExamQuestionLabelSchema,
      type: DBLegacyExamQuestionTypeSchema,
    })
  )
);
export const LegacyExamTypeAttrSchema = DBLegacyExamTypeAttrSchema;

export const LegacyExamSchema = z.object({
  subject: LegacyExamSubjectSchema,
  examId: LegacyExamExamIdSchema,
  active: LegacyExamActiveSchema.default(false),
  analytics: LegacyExamAnalyticsSchema.default({
    aggregateMark: 0,
    topMark: 0,
    uniqueAttempts: 0,
    average: 0,
  }),
  creationDatetime: LegacyExamCreationDatetimeSchema.default(() => dayjs().valueOf()),
  editDatetime: LegacyExamEditDatetimeSchema.default(() => dayjs().valueOf()),
  label: LegacyExamLabelSchema,
  maxMark: LegacyExamMaxMarkSchema,
  searchStr: LegacyExamSearchStrSchema.default(''),
  shortLabel: LegacyExamShortLabelSchema,
  topics: LegacyExamTopicsSchema.default({}),
  type: LegacyExamTypeAttrSchema,
});
